// src/modules/app-views/dataSearch/pages/Search/components/TypingText.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useRef, useEffect } from 'react';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * TypingText - Component that simulates typing effect like ChatGPT
 * Reveals text character by character with natural timing and auto-scroll
 */
const TypingText = ({
  text,
  speed = 12,
  onComplete,
  onProgress,
  className,
  style,
  scrollContainerRef,
  showCursor = true,
  as: Component = 'span',
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const textEndRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [displayedText, setDisplayedText] = useState('');
  const [isComplete, setIsComplete] = useState(false);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS I
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!text) {
      setDisplayedText('');
      setIsComplete(true);
      return;
    }

    setDisplayedText('');
    setIsComplete(false);
    let currentIndex = 0;

    const typeNextChar = () => {
      if (currentIndex < text.length) {
        const nextChar = text[currentIndex];
        setDisplayedText(text.slice(0, currentIndex + 1));
        currentIndex++;
        onProgress?.(currentIndex / text.length);

        // Auto-scroll: scroll the container to keep text visible (instant for typing)
        if (scrollContainerRef?.current) {
          const container = scrollContainerRef.current;
          container.scrollTop = container.scrollHeight;
        }

        setTimeout(typeNextChar, getNextDelay(nextChar, speed));
      } else {
        setIsComplete(true);
        onComplete?.();
      }
    };

    // Start typing with a small delay
    const startTimer = setTimeout(typeNextChar, 100);

    return () => clearTimeout(startTimer);
  }, [text, speed, onComplete, onProgress, scrollContainerRef]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. EFFECTS II
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 9. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Component className={className} style={style}>
      {displayedText}
      {showCursor && !isComplete && <span className="typing-cursor">|</span>}
      <span ref={textEndRef} />
    </Component>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default TypingText;

// *******************************************************************************
// HELPERS
// *******************************************************************************
/**
 * Variable speed for more natural feel
 */
function getNextDelay(char, baseSpeed) {
  // Pause longer on punctuation
  if (['.', '!', '?'].includes(char)) return baseSpeed * 4;
  if ([',', ';', ':'].includes(char)) return baseSpeed * 2;
  if (char === '\n') return baseSpeed * 3;
  // Random variation for natural feel
  return baseSpeed + Math.random() * (baseSpeed * 0.5);
}
